import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
// Select natif (pas de shadcn/ui select dans le projet)
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import { getAllRestaurants, getRestaurantById } from '@/data/mocks/restaurants';
import { getPreAuditReport } from '@/data/mocks/auditReport-new-restaurants';
import {
  FileCheck,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Target,
  Zap,
  Award,
  Download,
  Building2,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';

export const PreAuditProV3: React.FC = () => {
  const { selectedRestaurant, setSelectedRestaurant } = useSelectedRestaurant();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const currentRestaurant = getRestaurantById(selectedRestaurant);
  const auditReport = getPreAuditReport(selectedRestaurant);
  const allRestaurants = getAllRestaurants();

  // Helper: Nettoyer emojis MAIS garder accents français
  const cleanTextForPDF = (text: string): string => {
    return text
      // Enlever TOUS les emojis (range Unicode complet)
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
      .replace(/[\u{2600}-\u{26FF}]/gu, '')
      .replace(/[\u{2700}-\u{27BF}]/gu, '')
      // Emojis courants
      .replace(/[🚨⚠️✅💰📊❌✓🔒🎯💡🏛️🎭🥩🎵🌿👆🌍💼📈🤖⏱️🔧📞📧🎉⭐🍽️📜🎨🐟🍮🦞🔴🟡🟢📄🎬📝📍🔍💎⚡#]/g, '')
      // Ponctuation spéciale (GARDER accents é è à ê etc)
      .replace(/…/g, '...')
      .replace(/['']/g, "'")
      .replace(/[""]/g, '"')
      .replace(/[–—]/g, '-')
      // Symboles
      .replace(/★/g, '*')
      .replace(/•/g, '-')
      // Normaliser espaces
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Helper: Niveau d'investissement basé sur budget
  const getInvestmentLevel = (budget: string): string => {
    const amount = parseInt(budget.replace(/[^0-9]/g, ''));
    if (amount < 5000) return 'Faible';
    if (amount < 15000) return 'Modere';
    return 'Important';
  };

  const generateCleanPDF = async () => {
    if (!auditReport || !currentRestaurant) return;
    
    setIsGeneratingPDF(true);
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 15;
      let y = m;

      const newPage = () => {
        pdf.addPage();
        y = m;
        addFooter();
      };

      const addFooter = () => {
        // Ligne séparatrice
        pdf.setDrawColor(218, 165, 32);
        pdf.setLineWidth(0.5);
        pdf.line(m, ph - 15, pw - m, ph - 15);
        
        // Texte footer
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont('helvetica', 'normal');
        pdf.text('DOCUMENT CONFIDENTIEL - STRATOS Hospitality', m, ph - 8);
        pdf.text(`Page ${pdf.getCurrentPageInfo().pageNumber}`, pw - m, ph - 8, { align: 'right' });
      };

      const checkSpace = (needed: number) => {
        if (y + needed > ph - 25) {
          newPage();
          return true;
        }
        return false;
      };

      const addSectionHeader = (title: string) => {
        pdf.setFillColor(218, 165, 32);
        pdf.rect(0, y, pw, 14, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(title, m, y + 9);
        y += 20;
      };

      // ========== PAGE 1: COUVERTURE STYLÉE ==========
      // Fond dégradé simulé avec bandes
      for (let i = 0; i < 20; i++) {
        const shade = 250 - i * 5;
        pdf.setFillColor(shade, shade, shade);
        pdf.rect(0, i * (ph / 20), pw, ph / 20, 'F');
      }
      
      // Header doré premium
      pdf.setFillColor(218, 165, 32);
      pdf.rect(0, 0, pw, 55, 'F');
      
      // Ligne dorée en bas du header
      pdf.setFillColor(180, 130, 20);
      pdf.rect(0, 55, pw, 3, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(42);
      pdf.setFont('helvetica', 'bold');
      pdf.text('STRATOS', pw / 2, 28, { align: 'center' });
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('HOSPITALITY DIGITAL STRATEGY', pw / 2, 43, { align: 'center' });

      // Titre principal
      y = 80;
      pdf.setTextColor(40, 40, 40);
      pdf.setFontSize(26);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PRE-AUDIT STRATEGIQUE', pw / 2, y, { align: 'center' });
      
      y += 12;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Analyse de Visibilite Digitale Complete', pw / 2, y, { align: 'center' });

      // Nom restaurant avec encadré
      y += 25;
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(218, 165, 32);
      pdf.setLineWidth(2);
      pdf.roundedRect(m + 15, y, pw - 2 * m - 30, 22, 3, 3, 'FD');
      
      pdf.setTextColor(30, 30, 30);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const nameLines = pdf.splitTextToSize(currentRestaurant.name, pw - 2 * m - 40);
      pdf.text(nameLines[0], pw / 2, y + 14, { align: 'center' });

      // Score avec design premium
      y += 40;
      const scoreColor = auditReport.overallScore >= 70 ? [34, 197, 94] : 
                        auditReport.overallScore >= 40 ? [234, 179, 8] : [239, 68, 68];
      
      // Box score avec ombre
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(m + 25, y, pw - 2 * m - 50, 75, 8, 8, 'F');
      pdf.setDrawColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.setLineWidth(4);
      pdf.roundedRect(m + 25, y, pw - 2 * m - 50, 75, 8, 8, 'D');
      
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SCORE DE SANTE DIGITALE', pw / 2, y + 15, { align: 'center' });
      
      pdf.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.setFontSize(64);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${auditReport.overallScore}`, pw / 2 - 10, y + 48, { align: 'center' });
      
      pdf.setFontSize(24);
      pdf.setTextColor(150, 150, 150);
      pdf.text('/100', pw / 2 + 15, y + 48);
      
      pdf.setFontSize(13);
      pdf.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2]);
      pdf.setFont('helvetica', 'bold');
      const statusClean = auditReport.healthStatus === 'critical' ? 'SITUATION CRITIQUE' :
                         auditReport.healthStatus === 'warning' ? 'ATTENTION REQUISE' : 'BON ETAT';
      pdf.text(statusClean, pw / 2, y + 65, { align: 'center' });

      // Confidentiel stylé
      y += 95;
      pdf.setFillColor(254, 242, 242);
      pdf.roundedRect(m, y, pw - 2 * m, 32, 4, 4, 'F');
      pdf.setDrawColor(220, 38, 38);
      pdf.setLineWidth(1.5);
      pdf.roundedRect(m, y, pw - 2 * m, 32, 4, 4, 'D');
      
      pdf.setFontSize(11);
      pdf.setTextColor(185, 28, 28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DOCUMENT STRICTEMENT CONFIDENTIEL', pw / 2, y + 11, { align: 'center' });
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      const confLines = pdf.splitTextToSize(
        cleanTextForPDF('Ce document contient des informations strategiques confidentielles. Toute diffusion, reproduction ou utilisation non autorisee est strictement interdite. Propriete exclusive de STRATOS Hospitality.'),
        pw - 2 * m - 10
      );
      confLines.forEach((line: string, idx: number) => {
        pdf.text(line, pw / 2, y + 19 + idx * 4, { align: 'center' });
      });

      // Date seulement en bas (pas de contacts qui chevauchent)
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'italic');
      pdf.text(auditReport.auditDate, pw / 2, ph - 5, { align: 'center' });

      // ========== PAGE 2: RESUME EXECUTIF STYLÉ ==========
      newPage();
      addSectionHeader('1. RESUME EXECUTIF');

      // Situation avec encadré premium
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(m, y, pw - 2 * m, 35, 3, 3, 'F');
      pdf.setDrawColor(100, 116, 139);
      pdf.setLineWidth(0.5);
      pdf.roundedRect(m, y, pw - 2 * m, 35, 3, 3, 'D');
      
      pdf.setFontSize(10);
      pdf.setTextColor(51, 65, 85);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SITUATION ACTUELLE', m + 4, y + 7);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      const sitClean = cleanTextForPDF(auditReport.executiveSummary.situation);
      const sitLines = pdf.splitTextToSize(sitClean, pw - 2 * m - 8);
      sitLines.forEach((line: string, idx: number) => {
        pdf.text(line, m + 4, y + 14 + idx * 4);
      });
      
      y += 42;

      // Défis - Cards stylées
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DEFIS PRINCIPAUX IDENTIFIES', m, y);
      y += 8;

      auditReport.executiveSummary.mainChallenges.forEach((challenge, idx) => {
        checkSpace(16);
        
        // Card avec bordure gauche colorée
        pdf.setFillColor(255, 250, 250);
        pdf.roundedRect(m, y, pw - 2 * m, 14, 2, 2, 'F');
        
        // Bordure gauche rouge
        pdf.setFillColor(239, 68, 68);
        pdf.rect(m, y, 3, 14, 'F');
        
        // Numéro
        pdf.setFillColor(239, 68, 68);
        pdf.circle(m + 8, y + 7, 3, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${idx + 1}`, m + 8, y + 9, { align: 'center' });
        
        // Texte défi
        pdf.setFontSize(8.5);
        pdf.setTextColor(60, 60, 60);
        pdf.setFont('helvetica', 'normal');
        const cleanChallenge = cleanTextForPDF(challenge);
        const chalLines = pdf.splitTextToSize(cleanChallenge, pw - 2 * m - 18);
        chalLines.slice(0, 2).forEach((line: string, lineIdx: number) => {
          pdf.text(line, m + 13, y + 5 + lineIdx * 4);
        });
        
        y += 16;
      });

      // Impact Financier - Box premium
      y += 5;
      checkSpace(50);
      
      // Fond dégradé rouge simulé
      pdf.setFillColor(254, 242, 242);
      pdf.roundedRect(m, y, pw - 2 * m, 48, 4, 4, 'F');
      pdf.setDrawColor(239, 68, 68);
      pdf.setLineWidth(2);
      pdf.roundedRect(m, y, pw - 2 * m, 48, 4, 4, 'D');
      
      // Titre
      pdf.setTextColor(185, 28, 28);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('IMPACT FINANCIER DE L\'INACTION', pw / 2, y + 10, { align: 'center' });
      
      // Ligne séparatrice
      pdf.setDrawColor(239, 68, 68);
      pdf.setLineWidth(0.5);
      pdf.line(m + 15, y + 14, pw - m - 15, y + 14);
      
      // Perte
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Perte estimee actuelle :', m + 5, y + 22);
      
      pdf.setFontSize(14);
      pdf.setTextColor(220, 38, 38);
      pdf.setFont('helvetica', 'bold');
      const perte1 = cleanTextForPDF(auditReport.executiveSummary.estimatedLostRevenue);
      // Simplifier : juste "120k-180k" sans EUR ni /an
      const perteSimple = perte1.replace(/EUR/gi, 'k').replace(/\/an.*$/gi, '').replace(/k.*-/gi, 'k-').trim();
      pdf.text(`-${perteSimple}`, m + 5, y + 31);
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'normal');
      pdf.text('/an', m + 5, y + 36);
      
      // Potentiel
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Potentiel :', pw / 2 + 2, y + 22);
      
      pdf.setFontSize(14);
      pdf.setTextColor(5, 150, 105);
      pdf.setFont('helvetica', 'bold');
      const gain1 = cleanTextForPDF(auditReport.executiveSummary.potentialGain);
      // Prendre juste le max simplifié
      const gainMax = gain1.split('-').pop()?.split('/')[0]?.replace(/EUR/gi, 'k').replace(/avec.*$/gi, '').trim() || '250k';
      pdf.text(`+${gainMax}`, pw / 2 + 2, y + 31);
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'normal');
      pdf.text('/an', pw / 2 + 2, y + 36);
      
      // ROI
      y += 42;
      pdf.setFontSize(10);
      pdf.setTextColor(218, 165, 32);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`ROI STRATOS : ${auditReport.recommendedPack.roi}`, pw / 2, y, { align: 'center' });

      // ========== PAGE 2: METRIQUES ACTUELLES STYLÉES ==========
      newPage();
      addSectionHeader('2. VOS METRIQUES ACTUELLES');

      // Box SEO
      pdf.setFillColor(252, 248, 242);
      pdf.roundedRect(m, y, pw - 2 * m, 42, 3, 3, 'F');
      pdf.setDrawColor(218, 165, 32);
      pdf.setLineWidth(1);
      pdf.roundedRect(m, y, pw - 2 * m, 42, 3, 3, 'D');
      
      pdf.setFontSize(11);
      pdf.setTextColor(120, 80, 20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SEO & VISIBILITE ORGANIQUE', m + 4, y + 8);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      
      const seoMetrics = [
        ['Trafic Organique', `${auditReport.currentMetrics.seo.organicTraffic} visiteurs/mois`],
        ['Position Moyenne', `${auditReport.currentMetrics.seo.positionAverage} (Page ${Math.ceil(auditReport.currentMetrics.seo.positionAverage / 10)})`],
        ['Domain Authority', `${auditReport.currentMetrics.seo.domainAuthority}/100`],
        ['Backlinks', `${auditReport.currentMetrics.seo.backlinks} liens`],
        ['Visibilite', `${auditReport.currentMetrics.seo.visibilityScore}/100`]
      ];
      
      seoMetrics.forEach(([label, value], idx) => {
        const lineY = y + 16 + idx * 5;
        pdf.text(label, m + 4, lineY);
        pdf.setFont('helvetica', 'bold');
        pdf.text(value, pw - m - 4, lineY, { align: 'right' });
        pdf.setFont('helvetica', 'normal');
      });
      
      y += 48;

      // Box GEO
      pdf.setFillColor(240, 253, 244);
      pdf.roundedRect(m, y, pw - 2 * m, 37, 3, 3, 'F');
      pdf.setDrawColor(34, 197, 94);
      pdf.setLineWidth(1);
      pdf.roundedRect(m, y, pw - 2 * m, 37, 3, 3, 'D');
      
      pdf.setFontSize(11);
      pdf.setTextColor(5, 100, 60);
      pdf.setFont('helvetica', 'bold');
      pdf.text('GEO & REPUTATION LOCALE', m + 4, y + 8);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      
      const geoMetrics = [
        ['Note Google', `${auditReport.currentMetrics.geo.googleRating}/5 etoiles`],
        ['Nombre d\'avis', `${auditReport.currentMetrics.geo.reviewCount} avis`],
        ['Citations', `${auditReport.currentMetrics.geo.citations} citations`],
        ['GMB Completude', `${auditReport.currentMetrics.geo.gmbCompleteness}%`]
      ];
      
      geoMetrics.forEach(([label, value], idx) => {
        const lineY = y + 16 + idx * 5;
        pdf.text(label, m + 4, lineY);
        pdf.setFont('helvetica', 'bold');
        pdf.text(value, pw - m - 4, lineY, { align: 'right' });
        pdf.setFont('helvetica', 'normal');
      });
      
      y += 43;

      // Box Impact - Premium design
      checkSpace(55);
      
      // Fond avec bordure épaisse
      pdf.setFillColor(255, 245, 245);
      pdf.roundedRect(m, y, pw - 2 * m, 52, 4, 4, 'F');
      pdf.setDrawColor(220, 38, 38);
      pdf.setLineWidth(2.5);
      pdf.roundedRect(m, y, pw - 2 * m, 52, 4, 4, 'D');
      
      // Titre impact
      pdf.setFillColor(239, 68, 68);
      pdf.roundedRect(m + 8, y + 5, pw - 2 * m - 16, 10, 2, 2, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('IMPACT FINANCIER', pw / 2, y + 11.5, { align: 'center' });
      
      // Ligne séparatrice
      pdf.setDrawColor(239, 68, 68);
      pdf.setLineWidth(0.5);
      pdf.line(m + 15, y + 20, pw - m - 15, y + 20);
      
      // Perte vs Potentiel - 2 colonnes
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Perte estimee si inaction :', m + 8, y + 28);
      
      pdf.setFontSize(16);
      pdf.setTextColor(220, 38, 38);
      pdf.setFont('helvetica', 'bold');
      const perteClean = cleanTextForPDF(auditReport.executiveSummary.estimatedLostRevenue);
      pdf.text(`-${perteClean}`, m + 8, y + 37);
      
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'normal');
      pdf.text('par an', m + 8, y + 42);
      
      // Potentiel (droite)
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text('Potentiel recuperable :', pw / 2 + 5, y + 28);
      
      pdf.setFontSize(16);
      pdf.setTextColor(5, 150, 105);
      pdf.setFont('helvetica', 'bold');
      const gainClean = cleanTextForPDF(auditReport.executiveSummary.potentialGain);
      const gainValue = gainClean.split('-')[1]?.trim() || gainClean;
      pdf.text(`+${gainValue}`, pw / 2 + 5, y + 37);
      
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'normal');
      pdf.text('par an', pw / 2 + 5, y + 42);
      
      // ROI bas de box
      pdf.setFontSize(10);
      pdf.setTextColor(218, 165, 32);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`ROI STRATOS : ${auditReport.recommendedPack.roi}`, pw / 2, y + 48, { align: 'center' });

      // ========== PAGES AXES STRATEGIQUES (sans emojis) ==========
      auditReport.strategicAxes.forEach((axe, idx) => {
        newPage();
        
        // Titre
        pdf.setFillColor(218, 165, 32);
        pdf.rect(0, y, pw, 12, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        const axeTitleClean = cleanTextForPDF(axe.title);
        pdf.text(`${idx + 3}. ${axeTitleClean}`, m, y + 8);
        y += 18;

        // Description
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        pdf.setFont('helvetica', 'normal');
        const descClean = cleanTextForPDF(axe.description);
        const descLines = pdf.splitTextToSize(descClean, pw - 2 * m);
        descLines.slice(0, 3).forEach((line: string) => {
          pdf.text(line, m, y);
          y += 5;
        });

        // Situation vs Objectif
        y += 5;
        checkSpace(40);
        
        // Situation
        pdf.setFillColor(255, 245, 245);
        pdf.rect(m, y, (pw - 2 * m - 5) / 2, 25, 'F');
        pdf.setFontSize(9);
        pdf.setTextColor(185, 28, 28);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Situation Actuelle', m + 2, y + 5);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        const currentClean = cleanTextForPDF(axe.currentSituation);
        const currentLines = pdf.splitTextToSize(currentClean, (pw - 2 * m - 5) / 2 - 4);
        currentLines.slice(0, 3).forEach((line: string, i: number) => {
          pdf.text(line, m + 2, y + 10 + i * 4);
        });

        // Objectif
        pdf.setFillColor(240, 255, 240);
        pdf.rect(m + (pw - 2 * m + 5) / 2, y, (pw - 2 * m - 5) / 2, 25, 'F');
        pdf.setFontSize(9);
        pdf.setTextColor(5, 150, 105);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Objectif Cible', m + (pw - 2 * m + 5) / 2 + 2, y + 5);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        const targetClean = cleanTextForPDF(axe.targetObjective);
        const targetLines = pdf.splitTextToSize(targetClean, (pw - 2 * m - 5) / 2 - 4);
        targetLines.slice(0, 3).forEach((line: string, i: number) => {
          pdf.text(line, m + (pw - 2 * m + 5) / 2 + 2, y + 10 + i * 4);
        });

        y += 30;

        // Actions (sans emojis)
        pdf.setFontSize(10);
        pdf.setTextColor(50, 50, 50);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Plan d\'Action', m, y);
        y += 7;

        axe.actions.forEach((action: any, actionIdx: number) => {
          checkSpace(12);
          
          if (action.isVisible) {
            pdf.setFillColor(245, 255, 245);
            pdf.rect(m, y, pw - 2 * m, 10, 'F');
            
            pdf.setFontSize(9);
            pdf.setTextColor(5, 150, 105);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`${actionIdx + 1}.`, m + 2, y + 4);
            
            pdf.setTextColor(50, 50, 50);
            const actionTitleClean = cleanTextForPDF(action.title);
            pdf.text(actionTitleClean, m + 7, y + 4);
            
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 100, 100);
            const actionDescClean = cleanTextForPDF(action.description);
            const actionLines = pdf.splitTextToSize(actionDescClean, pw - 2 * m - 10);
            pdf.text(actionLines[0], m + 7, y + 8);
          } else {
            pdf.setFillColor(250, 250, 250);
            pdf.rect(m, y, pw - 2 * m, 10, 'F');
            
            pdf.setFontSize(9);
            pdf.setTextColor(150, 150, 150);
            pdf.setFont('helvetica', 'italic');
            const maskedClean = cleanTextForPDF(action.title);
            pdf.text(`${actionIdx + 1}. [RECOMMANDATION MASQUEE] ${maskedClean}`, m + 2, y + 4);
            
            pdf.setFontSize(8);
            pdf.text('Details dans accompagnement STRATOS Premium', m + 7, y + 8);
          }
          
          y += 12;
        });

        // Investissement simplifié (pas de montants qui font peur)
        y += 5;
        checkSpace(15);
        pdf.setFillColor(254, 252, 245);
        pdf.roundedRect(m, y, pw - 2 * m, 12, 2, 2, 'F');
        pdf.setDrawColor(218, 165, 32);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(m, y, pw - 2 * m, 12, 2, 2, 'D');
        
        const investLevel = getInvestmentLevel(axe.budget);
        const levelColor = investLevel === 'Faible' ? [34, 197, 94] : 
                          investLevel === 'Modere' ? [234, 179, 8] : [239, 68, 68];
        
        pdf.setFontSize(9);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Delai :', m + 3, y + 5);
        
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(60, 60, 60);
        pdf.text(axe.timeline, m + 15, y + 5);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('Investissement :', m + 3, y + 10);
        
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(levelColor[0], levelColor[1], levelColor[2]);
        pdf.text(investLevel, m + 30, y + 10);
      });

      // ========== PAGE FINALE: CTA STYLÉE ==========
      newPage();
      
      // Fond doré premium
      pdf.setFillColor(218, 165, 32);
      pdf.rect(0, y - 20, pw, ph - y + 20, 'F');
      
      // Titre accrocheur
      y += 30;
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(32);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Pret a Transformer', pw / 2, y, { align: 'center' });
      y += 15;
      pdf.text('Votre Visibilite Digitale ?', pw / 2, y, { align: 'center' });
      
      // Box blanche centrale
      y += 25;
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(m + 10, y, pw - 2 * m - 20, 85, 4, 4, 'F');
      
      // Pack
      pdf.setTextColor(40, 40, 40);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(auditReport.recommendedPack.name, pw / 2, y + 15, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont('helvetica', 'normal');
      const priceClean = cleanTextForPDF(auditReport.recommendedPack.price);
      pdf.text(priceClean, pw / 2, y + 25, { align: 'center' });
      
      pdf.setFontSize(10);
      pdf.setTextColor(120, 120, 120);
      pdf.text(auditReport.recommendedPack.duration, pw / 2, y + 33, { align: 'center' });
      
      // Ligne séparatrice
      pdf.setDrawColor(218, 165, 32);
      pdf.setLineWidth(1);
      pdf.line(m + 25, y + 40, pw - m - 25, y + 40);
      
      // ROI
      pdf.setFontSize(22);
      pdf.setTextColor(218, 165, 32);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`ROI : ${auditReport.recommendedPack.roi}`, pw / 2, y + 53, { align: 'center' });
      
      // Services inclus
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Strategie complete, execution, monitoring, reporting', pw / 2, y + 62, { align: 'center' });
      pdf.text('Chatbot IA 24/7, Site dedie, Optimisation GEO...', pw / 2, y + 68, { align: 'center' });
      pdf.text('+ 15 autres services detailles dans la proposition', pw / 2, y + 74, { align: 'center' });

      // Contact - Blanc sur doré
      y += 100;
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('CONTACT', pw / 2, y, { align: 'center' });
      
      y += 8;
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Adrien Cerdan', pw / 2, y, { align: 'center' });
      
      y += 6;
      pdf.setFontSize(10);
      pdf.text('admin@stratos-hospitality.com', pw / 2, y, { align: 'center' });
      
      y += 6;
      pdf.text('+33 6 64 27 60 32', pw / 2, y, { align: 'center' });
      
      y += 10;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Reponse sous 24h - Kick-off possible sous 7 jours', pw / 2, y, { align: 'center' });

      // Save
      const cleanName = currentRestaurant.name.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `STRATOS_PreAudit_${cleanName}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Erreur PDF:', error);
      alert('Erreur generation PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (!auditReport) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Selectionnez un restaurant</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {allRestaurants.map((resto) => (
              <Button
                key={resto.id}
                onClick={() => setSelectedRestaurant(resto.slug)}
                variant="outline"
                className="w-full justify-start"
              >
                {resto.name}
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalP0 = auditReport.categoryScores.reduce((sum, cat) => sum + cat.p0Count, 0);
  const totalP1 = auditReport.categoryScores.reduce((sum, cat) => sum + cat.p1Count, 0);
  const totalP2 = auditReport.categoryScores.reduce((sum, cat) => sum + cat.p2Count, 0);

  return (
    <div className="space-y-6">
      {/* Header avec sélecteur */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[300px]">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Pre-Audit Strategique
            </h1>
            <p className="text-gray-600">
              Analyse complete visibilite digitale
            </p>
          </div>
          
          {/* Sélecteur Restaurant - Boutons Stylés */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              {allRestaurants.filter(r => r.slug === 'fontaine-gaillon' || r.slug === 'hollywood-savoy').map((resto) => (
                <button
                  key={resto.id}
                  onClick={() => setSelectedRestaurant(resto.slug)}
                  className={`px-4 py-2 rounded-md font-semibold transition-all ${
                    selectedRestaurant === resto.slug
                      ? 'bg-stratos-gold text-gray-900 shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span className="text-sm">{resto.name}</span>
                  </div>
                </button>
              ))}
            </div>

            <Button 
              onClick={generateCleanPDF}
              disabled={isGeneratingPDF}
              size="lg"
              className="bg-stratos-gold hover:bg-yellow-600 text-gray-900 font-bold shadow-lg"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generation...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Export PDF
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Score */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        <Card className={`border-4 ${auditReport.overallScore < 50 ? 'border-red-500' : 'border-yellow-500'}`}>
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-4">
              <div className={`text-8xl font-bold ${auditReport.overallScore < 50 ? 'text-red-600' : 'text-yellow-600'}`}>
                {auditReport.overallScore}
                <span className="text-4xl text-gray-400">/100</span>
              </div>
              <Progress value={auditReport.overallScore} className="h-6 max-w-md mx-auto" />
              <p className="text-2xl font-bold">
                {auditReport.healthStatus === 'critical' && <Badge variant="destructive" className="text-lg">SITUATION CRITIQUE</Badge>}
                {auditReport.healthStatus === 'warning' && <Badge className="bg-yellow-500 text-white text-lg">ATTENTION REQUISE</Badge>}
              </p>
              <p className="text-gray-600">
                {totalP0} critiques • {totalP1} importants • {totalP2} ameliorations
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Impact Financier */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-2 border-red-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <TrendingDown className="w-5 h-5" />
                Perte Estimee
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-red-600">
                {auditReport.executiveSummary.estimatedLostRevenue}
              </p>
              <p className="text-sm text-gray-600 mt-2">/an si inaction</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <TrendingUp className="w-5 h-5" />
                Potentiel Recuperable
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">
                {auditReport.executiveSummary.potentialGain.split('-')[1] || auditReport.executiveSummary.potentialGain}
              </p>
              <p className="text-sm text-gray-600 mt-2">/an avec STRATOS</p>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Resume Executif */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
        <Card>
          <CardHeader>
            <CardTitle>Situation Actuelle</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed mb-6">
              {auditReport.executiveSummary.situation}
            </p>
            
            <h3 className="font-bold text-gray-900 mb-3">Defis Principaux</h3>
            <div className="space-y-2">
              {auditReport.executiveSummary.mainChallenges.map((challenge, idx) => (
                <div key={idx} className="flex items-start gap-2 p-3 bg-red-50 rounded border-l-4 border-red-500">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-800">{challenge}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Axes Strategiques */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
        <Card>
          <CardHeader>
            <CardTitle>Axes Strategiques d'Amelioration</CardTitle>
            <CardDescription>
              {auditReport.strategicAxes.length} leviers identifies - {auditReport.strategicAxes.filter(a => a.priority === 'P0').length} critiques
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {auditReport.strategicAxes.map((axe, idx) => (
              <Card key={axe.id} className={`border-2 ${axe.priority === 'P0' ? 'border-red-500' : 'border-yellow-500'}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={axe.priority === 'P0' ? 'destructive' : 'default'} className="font-mono">
                          {axe.priority}
                        </Badge>
                        <span className="text-sm text-gray-600">{axe.timeline}</span>
                      </div>
                      <CardTitle className="text-lg">{axe.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-700">{axe.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="p-3 bg-red-50 rounded border-l-4 border-red-500">
                      <p className="text-xs font-bold text-red-700 mb-1">Situation</p>
                      <p className="text-xs text-gray-700">{axe.currentSituation}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded border-l-4 border-green-500">
                      <p className="text-xs font-bold text-green-700 mb-1">Objectif</p>
                      <p className="text-xs text-gray-700">{axe.targetObjective}</p>
                    </div>
                  </div>

                  <div>
                    <p className="font-bold text-sm mb-2">Actions Proposees</p>
                    <div className="space-y-2">
                      {axe.actions.map((action: any, actionIdx: number) => (
                        <div 
                          key={actionIdx}
                          className={`p-3 rounded ${action.isVisible ? 'bg-green-50 border border-green-200' : 'bg-gray-100 border border-gray-300'}`}
                        >
                          <div className="flex items-start gap-2">
                            {action.isVisible ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5" />
                            ) : (
                              <span className="text-xs text-gray-500 mt-0.5">🔒</span>
                            )}
                            <div className="flex-1">
                              <p className="font-semibold text-sm">{action.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{action.description}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-3 border-t">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">Impact</p>
                      <p className="text-sm font-bold text-gray-900">{axe.estimatedImpact.substring(0, 20)}...</p>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">Timeline</p>
                      <p className="text-sm font-bold text-blue-600">{axe.timeline}</p>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">Budget</p>
                      <p className="text-sm font-bold text-gray-900">{axe.budget.substring(0, 15)}...</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </motion.div>

      {/* CTA Final */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Card className="bg-gradient-to-r from-stratos-gold to-yellow-600 border-0 text-white">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-4">
              <h3 className="text-3xl font-bold">
                Pret a Transformer Votre Visibilite ?
              </h3>
              <p className="text-xl">
                {auditReport.recommendedPack.name} - {auditReport.recommendedPack.price}
              </p>
              <p className="text-2xl font-bold">
                ROI : {auditReport.recommendedPack.roi}
              </p>
              <Button
                onClick={generateCleanPDF}
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 text-xl px-10 py-6 mt-4"
              >
                <Download className="w-6 h-6 mr-2" />
                Telecharger le Pre-Audit
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PreAuditProV3;
